import React from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";

const PermissionMatrix = () => {
  const { t } = useTranslation();

  const AllowedIcon = () => <CheckIcon sx={{ color: "info.main", fontSize: 20 }} />;

  const NotAllowedIcon = () => <CloseIcon sx={{ color: "error.main", fontSize: 20 }} />;

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t("preferences.enterprise.permissions.title")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("preferences.enterprise.permissions.description")}
        </Typography>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "grey.900" }}>
              <TableCell sx={{ minWidth: 250 }}>{t("preferences.enterprise.permissions.permission")}</TableCell>
              <TableCell align="center" sx={{ minWidth: 120 }}>
                {t("preferences.enterprise.role.owner")}
              </TableCell>
              <TableCell align="center" sx={{ minWidth: 120 }}>
                {t("preferences.enterprise.role.manager")}
              </TableCell>
              <TableCell align="center" sx={{ minWidth: 120 }}>
                {t("preferences.enterprise.role.member")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* 組織管理 */}
            <TableRow>
              <TableCell colSpan={4} sx={{ backgroundColor: "grey.800", color: "white", fontWeight: "bold", py: 0.4 }}>
                {t("preferences.enterprise.permissions.organizationManagement")}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ pl: 4 }}>{t("preferences.enterprise.permissions.organizationSettings")}</TableCell>
              <TableCell align="center">
                <AllowedIcon />
              </TableCell>
              <TableCell align="center">
                <NotAllowedIcon />
              </TableCell>
              <TableCell align="center">
                <NotAllowedIcon />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ pl: 4 }}>{t("preferences.enterprise.permissions.organizationNameChange")}</TableCell>
              <TableCell align="center">
                <AllowedIcon />
              </TableCell>
              <TableCell align="center">
                <NotAllowedIcon />
              </TableCell>
              <TableCell align="center">
                <NotAllowedIcon />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ pl: 4 }}>{t("preferences.enterprise.permissions.contractManagement")}</TableCell>
              <TableCell align="center">
                <AllowedIcon />
              </TableCell>
              <TableCell align="center">
                <NotAllowedIcon />
              </TableCell>
              <TableCell align="center">
                <NotAllowedIcon />
              </TableCell>
            </TableRow>

            {/* メンバー管理 */}
            <TableRow>
              <TableCell colSpan={4} sx={{ backgroundColor: "grey.800", color: "white", fontWeight: "bold", py: 0.4 }}>
                {t("preferences.enterprise.permissions.memberManagement")}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ pl: 4 }}>{t("preferences.enterprise.permissions.inviteMembers")}</TableCell>
              <TableCell align="center">
                <AllowedIcon />
              </TableCell>
              <TableCell align="center">
                <AllowedIcon />
              </TableCell>
              <TableCell align="center">
                <NotAllowedIcon />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ pl: 4 }}>{t("preferences.enterprise.permissions.removeMembers")}</TableCell>
              <TableCell align="center">
                <AllowedIcon />
              </TableCell>
              <TableCell align="center">
                <AllowedIcon />
              </TableCell>
              <TableCell align="center">
                <NotAllowedIcon />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ pl: 4 }}>{t("preferences.enterprise.permissions.changeRoles")}</TableCell>
              <TableCell align="center">
                <AllowedIcon />
              </TableCell>
              <TableCell align="center">
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                  <AllowedIcon />
                  <Typography variant="caption" color="text.secondary">
                    *
                  </Typography>
                </Box>
              </TableCell>
              <TableCell align="center">
                <NotAllowedIcon />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ pl: 4 }}>{t("preferences.enterprise.permissions.manageManagers")}</TableCell>
              <TableCell align="center">
                <AllowedIcon />
              </TableCell>
              <TableCell align="center">
                <NotAllowedIcon />
              </TableCell>
              <TableCell align="center">
                <NotAllowedIcon />
              </TableCell>
            </TableRow>

            {/* 招待管理 */}
            <TableRow>
              <TableCell colSpan={4} sx={{ backgroundColor: "grey.800", color: "white", fontWeight: "bold", py: 0.4 }}>
                {t("preferences.enterprise.permissions.invitationManagement")}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ pl: 4 }}>{t("preferences.enterprise.permissions.sendInvitations")}</TableCell>
              <TableCell align="center">
                <AllowedIcon />
              </TableCell>
              <TableCell align="center">
                <AllowedIcon />
              </TableCell>
              <TableCell align="center">
                <NotAllowedIcon />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ pl: 4 }}>{t("preferences.enterprise.permissions.resendInvitations")}</TableCell>
              <TableCell align="center">
                <AllowedIcon />
              </TableCell>
              <TableCell align="center">
                <AllowedIcon />
              </TableCell>
              <TableCell align="center">
                <NotAllowedIcon />
              </TableCell>
            </TableRow>

            {/* レポート・分析 */}
            <TableRow>
              <TableCell colSpan={4} sx={{ backgroundColor: "grey.800", color: "white", fontWeight: "bold", py: 0.4 }}>
                {t("preferences.enterprise.permissions.reportsAndAnalytics")}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ pl: 4 }}>{t("preferences.enterprise.permissions.viewUsageReports")}</TableCell>
              <TableCell align="center">
                <AllowedIcon />
              </TableCell>
              <TableCell align="center">
                <AllowedIcon />
              </TableCell>
              <TableCell align="center">
                <NotAllowedIcon />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ pl: 4 }}>{t("preferences.enterprise.permissions.viewAllMemberUsage")}</TableCell>
              <TableCell align="center">
                <AllowedIcon />
              </TableCell>
              <TableCell align="center">
                <AllowedIcon />
              </TableCell>
              <TableCell align="center">
                <NotAllowedIcon />
              </TableCell>
            </TableRow>

            {/* サービス利用 */}
            <TableRow>
              <TableCell colSpan={4} sx={{ backgroundColor: "grey.800", color: "white", fontWeight: "bold", py: 0.4 }}>
                {t("preferences.enterprise.permissions.serviceUsage")}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ pl: 4 }}>{t("preferences.enterprise.permissions.useAIFeatures")}</TableCell>
              <TableCell align="center">
                <AllowedIcon />
              </TableCell>
              <TableCell align="center">
                <AllowedIcon />
              </TableCell>
              <TableCell align="center">
                <AllowedIcon />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ pl: 4 }}>{t("preferences.enterprise.permissions.useCredits")}</TableCell>
              <TableCell align="center">
                <AllowedIcon />
              </TableCell>
              <TableCell align="center">
                <AllowedIcon />
              </TableCell>
              <TableCell align="center">
                <AllowedIcon />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ pl: 4 }}>{t("preferences.enterprise.permissions.viewOwnUsage")}</TableCell>
              <TableCell align="center">
                <AllowedIcon />
              </TableCell>
              <TableCell align="center">
                <AllowedIcon />
              </TableCell>
              <TableCell align="center">
                <AllowedIcon />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          * マネージャーはメンバーのロール変更のみ可能（オーナー・マネージャーの変更は不可）
        </Typography>
      </Box>
    </Box>
  );
};

export default PermissionMatrix;
